import getConfig from 'next/config';
import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { useApolloClient } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import styles from './style';
import { useToggle } from '../../hooks/useToggle';
import {
  fetchSimTypes,
  handleSimTypeUpdate,
  mapApiToInternalSimType,
  mapInternalToApiSimType,
} from './simtypeSelector.util';
import ApiFailureModal from '../eir-error-modal/ApiFailureModal';
import ErrorModal from '../eir-error-modal/ErrorModal';
import { handleOk } from '../eir-mobile-offer-container/mobileFunctionContainer';

const { publicRuntimeConfig } = getConfig();

const SimoSimTypeSelector = ({ cmsData, onSelectType, offerId }) => {
  const theme = useTheme();
  const graphql = useApolloClient();
  const [selectedTypeApiValue, setSelectedTypeApiValue] = useState(null);
  const [selectedSIM, setSelectedSIM] = useState(null);
  const [updatingSIMType, setUpdatingSIMType] = useState(false);
  const [simTypes, setSimTypes] = useState([]);
  const openAPIFailureModal = useToggle(false);
  const [openError, setOpenError] = useState(false);
  const [errorModalOccurence, setErrorModalOccurence] = useState(0);
  const errorModalMaxOccurence = 1;
  const errorModal = useToggle(false);

  useEffect(() => {
    const loadSimTypes = async () => {
      const apiType = await fetchSimTypes(graphql);
      setSimTypes(apiType);
    };
    loadSimTypes();
  }, []);
  let SIMUpdateSuccess;

  const handleSelect = async type => {
    if (!updatingSIMType) {
      setUpdatingSIMType(true);
      const apiValue = mapInternalToApiSimType[type];
      if (apiValue === selectedSIM) {
        setUpdatingSIMType(false);
        return;
      }
      if (apiValue === 'ALL_IN_1' || apiValue === 'ESIM') {
        SIMUpdateSuccess = await handleSimTypeUpdate({
          SIMType: apiValue,
          offerId,
          offerCode: '',
          graphql,
          url: '/shop/mobile',
        });
      }
      if (SIMUpdateSuccess) {
        setUpdatingSIMType(false);
        setSelectedSIM(apiValue);
        setSelectedTypeApiValue(apiValue);
        onSelectType(apiValue);
      } else {
        setUpdatingSIMType(false);
        setOpenError(true);
        errorModal.enable();
        setErrorModalOccurence(errorModalOccurence + 1);
        openAPIFailureModal.enable();
      }
    }
  };
  return (
    <Box sx={styles(theme).container}>
      <Typography variant='h2' sx={styles(theme).heading}>
        {cmsData?.attributes?.Mobile_SimSelector?.SimSelectorHeading}
      </Typography>
      <Grid container spacing={2} justifyContent='center' alignItems='stretch'>
        {simTypes.includes('ALL_IN_1') && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ height: '100%' }}>
              <Paper
                variant='outlined'
                sx={{
                  height: '100%',
                  ...styles(theme).option,
                  ...(selectedTypeApiValue === 'ALL_IN_1' ? styles(theme).selected : null),
                }}
                tabIndex={0}
                onClick={async () => handleSelect(mapApiToInternalSimType.ALL_IN_1)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(mapApiToInternalSimType.ALL_IN_1);
                  }
                }}
              >
                <Box sx={styles(theme).optionContent}>
                  <Box sx={styles(theme).buttonIcon}>
                    {selectedTypeApiValue === 'ALL_IN_1' ? (
                      <img
                        src={`${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${cmsData?.attributes?.Mobile_SimSelector?.pSIM?.selectedIcon?.data?.attributes?.url}`}
                        alt=''
                        style={styles(theme).inlineIcon}
                      />
                    ) : (
                      <img
                        src={`${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${cmsData?.attributes?.Mobile_SimSelector?.pSIM?.icon?.data?.attributes?.url}`}
                        alt=''
                        style={styles(theme).inlineIcon}
                      />
                    )}
                  </Box>
                  <Box sx={styles(theme).textContainer}>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        ...styles(theme).buttonTitle,
                        ...(selectedTypeApiValue === 'ALL_IN_1'
                          ? styles(theme).selectedText
                          : null),
                      }}
                    >
                      {cmsData?.attributes?.Mobile_SimSelector?.pSIM?.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        ...styles(theme).buttonDescription,
                        ...(selectedTypeApiValue === 'ALL_IN_1'
                          ? styles(theme).selectedText
                          : null),
                      }}
                    >
                      {cmsData?.attributes?.Mobile_SimSelector?.pSIM?.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        )}
        {simTypes.includes('ESIM') && (
          <Grid item xs={12} sm={6}>
            <Box sx={{ height: '100%' }}>
              <Paper
                variant='outlined'
                sx={{
                  height: '100%',
                  ...styles(theme).option,
                  ...(selectedTypeApiValue === 'ESIM' ? styles(theme).selected : null),
                }}
                tabIndex={0}
                onClick={async () => handleSelect(mapApiToInternalSimType.ESIM)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(mapApiToInternalSimType.ESIM);
                  }
                }}
              >
                <Box sx={styles(theme).optionContent}>
                  <Box sx={styles(theme).buttonIcon}>
                    {selectedTypeApiValue === 'ESIM' ? (
                      <img
                        src={`${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${cmsData?.attributes?.Mobile_SimSelector?.eSIM?.selectedIcon?.data?.attributes?.url}`}
                        alt=''
                        style={styles(theme).inlineIcon}
                      />
                    ) : (
                      <img
                        src={`${publicRuntimeConfig.NEXT_PUBLIC_STRAPI}${cmsData?.attributes?.Mobile_SimSelector?.eSIM?.icon?.data?.attributes?.url}`}
                        alt=''
                        style={styles(theme).inlineIcon}
                      />
                    )}
                  </Box>
                  <Box sx={styles(theme).textContainer}>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        ...styles(theme).buttonTitle,
                        ...(selectedTypeApiValue === 'ESIM' ? styles(theme).selectedText : null),
                      }}
                    >
                      {cmsData?.attributes?.Mobile_SimSelector?.eSIM?.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        ...styles(theme).buttonDescription,
                        ...(selectedTypeApiValue === 'ESIM' ? styles(theme).selectedText : null),
                      }}
                    >
                      {cmsData?.attributes?.Mobile_SimSelector?.eSIM?.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        )}
        {openError && errorModalOccurence > errorModalMaxOccurence && (
          <ApiFailureModal openAPIFailureModal={openAPIFailureModal} handleOk={handleOk} />
        )}
        {errorModal.value && (
          <ErrorModal
            errorBBModal={errorModal}
            occurences={errorModalMaxOccurence}
            currentOccurence={errorModalOccurence}
            ctaMethod={() => {}}
          />
        )}
      </Grid>
    </Box>
  );
};

export default SimoSimTypeSelector;
